import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Modal, Table, InputGroup } from 'react-bootstrap';
import { FaUserPlus, FaSearch, FaEdit, FaTrash, FaEye, FaTimes, FaMapMarkerAlt } from 'react-icons/fa';
import { clientsAPI, sitesAPI } from '../../utils/api';
import notificationService from '../../services/notificationService';
import { decimalToDMS } from '../../utils/gpsUtils';
import '../../styles/modern-design-system.css';
import './PersonnelDashboard.css';

const NewClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingClientId, setEditingClientId] = useState(null);
  const [locationFetching, setLocationFetching] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    siteName: '',
    name: '',
    phone: '',
    contactPerson: '',
    contactPhone: '',
    branch: '',
    region: '',
    physicalLocation: '',
    locationName: '',
    locationCoords: '',
    description: ''
  });

  // Fetch clients and sites on component mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const clientsResponse = await clientsAPI.getMyClients();
      
      // Handle different response formats
      const clientsData = Array.isArray(clientsResponse.data) 
        ? clientsResponse.data 
        : (clientsResponse.data?.clients || clientsResponse.data?.data || []);
      
      setClients(clientsData);
      setError('');
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const captureLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser.');
      return;
    }
    setLocationFetching(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const latDMS = decimalToDMS(latitude, true);
        const lngDMS = decimalToDMS(longitude, false);
        const dmsString = `${latDMS} ${lngDMS}`;
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
            { headers: { 'Accept-Language': 'en' } }
          );
          const data = await res.json();
          const addr = data.address || {};
          const parts = [
            addr.amenity || addr.building || addr.shop || addr.office,
            addr.road || addr.pedestrian || addr.footway,
            addr.neighbourhood || addr.suburb || addr.quarter,
            addr.city || addr.town || addr.village || addr.county
          ].filter(Boolean);
          const name = parts.length > 0 ? parts.join(', ') : (data.display_name || `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`);
          setFormData(prev => ({ ...prev, physicalLocation: name, locationName: name, locationCoords: dmsString }));
        } catch (err) {
          const fallback = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
          setFormData(prev => ({
            ...prev,
            physicalLocation: fallback,
            locationName: fallback,
            locationCoords: dmsString
          }));
        } finally {
          setLocationFetching(false);
        }
      },
      () => {
        setLocationFetching(false);
        setError('Unable to retrieve your location. Please check browser permissions.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      setError('Client name is required');
      return;
    }
    if (!formData.phone.trim()) {
      setError('Phone number is required');
      return;
    }
    if (!formData.siteName.trim()) {
      setError('Site name is required');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      
      if (isEditing) {
        // Update existing client
        await clientsAPI.update(editingClientId, formData);
        setSuccess('Client updated successfully!');
      } else {
        // Create new client
        const response = await clientsAPI.create(formData);
        
        // Trigger notification to admin and sub-admins
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        notificationService.triggerClientAction('client-added', {
          personnelName: currentUser?.name || currentUser?.username || 'Personnel',
          personnelId: currentUser?.id || currentUser?.userId,
          clientName: formData.name,
          siteName: formData.siteName,
          branch: formData.branch,
          region: formData.region
        });
        
        setSuccess('Client added successfully!');
      }
      
      handleCloseModal();
      
      // Refresh clients list
      await fetchData();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} client:`, err);
      setError(err.response?.data?.message || `Failed to ${isEditing ? 'update' : 'add'} client. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewDetails = (client) => {
    setSelectedClient(client);
    setShowDetailsModal(true);
  };

  const handleEdit = (client) => {
    setIsEditing(true);
    setEditingClientId(client.id);
    setFormData({
      siteName: client.siteName || '',
      name: client.name,
      phone: client.phone,
      contactPerson: client.contactPerson || '',
      contactPhone: client.contactPhone || '',
      branch: client.branch || '',
      region: client.region || '',
      physicalLocation: client.physicalLocation || '',
      locationName: client.locationName || '',
      locationCoords: client.locationCoords || '',
      description: client.description || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) {
      return;
    }

    try {
      await clientsAPI.delete(clientId);
      setSuccess('Client deleted successfully!');
      await fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting client:', err);
      setError('Failed to delete client. Please try again.');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsEditing(false);
    setEditingClientId(null);
    setFormData({
      siteName: '',
      name: '',
      phone: '',
      contactPerson: '',
      contactPhone: '',
      branch: '',
      region: '',
      physicalLocation: '',
      locationName: '',
      locationCoords: '',
      description: ''
    });
  };

  // Filter clients based on search term
  const filteredClients = clients.filter(client => 
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.phone.includes(searchTerm) ||
    client.siteName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container fluid className="py-3 py-md-4">
      <Row className="mb-3 mb-md-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h1 className="mb-2" style={{
                fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                fontWeight: 700,
                color: 'var(--text-primary)'
              }}>
                New Clients
              </h1>
              <p style={{ color: 'var(--text-muted)', marginBottom: 0 }}>
                Manage your client information
              </p>
            </div>
            <Button 
              variant="primary" 
              onClick={() => setShowModal(true)}
              className="d-flex align-items-center gap-2"
              style={{
                padding: '0.625rem 1.5rem',
                fontWeight: 600
              }}
            >
              <FaUserPlus />
              Add New Client
            </Button>
          </div>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      {/* Search Bar */}
      <Row className="mb-4">
        <Col md={6}>
          <div style={{ position: 'relative' }}>
            <FaSearch style={{
              position: 'absolute',
              left: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)'
            }} />
            <Form.Control
              type="text"
              placeholder="Search clients by name, phone, or site..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                paddingLeft: '2.5rem',
                background: 'var(--surface-glass)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-lg)'
              }}
            />
          </div>
        </Col>
      </Row>

      {/* Clients Table */}
      <Card className="shadow-sm" style={{
        background: 'var(--surface-glass)',
        backdropFilter: 'blur(10px)',
        border: '1px solid var(--border-color)',
        borderRadius: 'var(--radius-xl)'
      }}>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" style={{ color: 'var(--accent-blue)' }} />
              <p className="mt-3" style={{ color: 'var(--text-muted)' }}>Loading clients...</p>
            </div>
          ) : filteredClients.length > 0 ? (
            <div className="table-responsive">
              <Table hover className="modern-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th className="d-none d-md-table-cell">Site</th>
                    <th className="d-none d-lg-table-cell">Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((client) => (
                    <tr key={client.id}>
                      <td>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                          {client.name}
                        </div>
                        <div className="d-md-none" style={{ 
                          fontSize: 'var(--font-size-xs)', 
                          color: 'var(--text-muted)',
                          marginTop: '0.25rem'
                        }}>
                          {client.siteName}
                        </div>
                      </td>
                      <td style={{ color: 'var(--text-secondary)' }}>
                        {client.phone}
                      </td>
                      <td className="d-none d-md-table-cell" style={{ color: 'var(--text-secondary)' }}>
                        {client.siteName}
                      </td>
                      <td className="d-none d-lg-table-cell" style={{ color: 'var(--text-secondary)' }}>
                        {client.description ? (
                          client.description.length > 50 
                            ? `${client.description.substring(0, 50)}...` 
                            : client.description
                        ) : (
                          <span style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>
                            No description
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleViewDetails(client)}
                            title="View Details"
                          >
                            <FaEye />
                          </Button>
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => handleEdit(client)}
                            title="Edit"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(client.id)}
                            title="Delete"
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-5">
              <FaUserPlus size={64} style={{ color: 'var(--text-muted)', opacity: 0.3 }} />
              <p className="mt-3" style={{ color: 'var(--text-muted)' }}>
                {searchTerm ? 'No clients found matching your search.' : 'No clients added yet. Click "Add New Client" to get started.'}
              </p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add/Edit Client Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton style={{
          background: 'var(--surface-glass)',
          borderBottom: '1px solid var(--border-color)'
        }}>
          <Modal.Title style={{ color: 'var(--text-primary)', fontWeight: 700 }}>
            {isEditing ? <FaEdit className="me-2" /> : <FaUserPlus className="me-2" />}
            {isEditing ? 'Edit Client' : 'Add New Client'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: 'var(--bg-primary)' }}>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                Site Name <span style={{ color: 'var(--error-red)' }}>*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="siteName"
                value={formData.siteName}
                onChange={handleInputChange}
                placeholder="Enter site name"
                required
                style={{
                  background: 'var(--surface-glass)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)'
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                Client Name <span style={{ color: 'var(--error-red)' }}>*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter client name"
                required
                style={{
                  background: 'var(--surface-glass)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)'
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                Phone Number <span style={{ color: 'var(--error-red)' }}>*</span>
              </Form.Label>
              <Form.Control
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Enter phone number"
                required
                style={{
                  background: 'var(--surface-glass)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)'
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                Contact Person
              </Form.Label>
              <Form.Control
                type="text"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleInputChange}
                placeholder="Enter contact person name"
                style={{
                  background: 'var(--surface-glass)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)'
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                Contact Phone Number
              </Form.Label>
              <Form.Control
                type="tel"
                name="contactPhone"
                value={formData.contactPhone}
                onChange={handleInputChange}
                placeholder="Enter contact person phone number"
                style={{
                  background: 'var(--surface-glass)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)'
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                Branch
              </Form.Label>
              <Form.Control
                type="text"
                name="branch"
                value={formData.branch}
                onChange={handleInputChange}
                placeholder="Enter branch name"
                style={{
                  background: 'var(--surface-glass)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)'
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                Region
              </Form.Label>
              <Form.Control
                type="text"
                name="region"
                value={formData.region}
                onChange={handleInputChange}
                placeholder="Enter region"
                style={{
                  background: 'var(--surface-glass)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)'
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                Physical Location
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  name="physicalLocation"
                  value={formData.physicalLocation}
                  onChange={handleInputChange}
                  placeholder="Enter physical location (e.g., Building A, Floor 3)"
                  style={{
                    background: 'var(--surface-glass)',
                    border: '1px solid var(--border-color)',
                    color: 'var(--text-primary)'
                  }}
                />
                <Button
                  variant="outline-secondary"
                  onClick={captureLocation}
                  disabled={locationFetching}
                  title="Capture current GPS location"
                  style={{ borderColor: 'var(--border-color)' }}
                >
                  {locationFetching ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FaMapMarkerAlt style={{ color: 'var(--accent-blue)' }} />
                  )}
                </Button>
              </InputGroup>
              <Form.Text style={{ color: 'var(--text-muted)' }}>
                Specific location within the site. Tap 📍 to auto-capture GPS location.
              </Form.Text>
              {formData.locationCoords && (
                <div className="mt-1" style={{ color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                  📍 {formData.locationCoords}
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                Description
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter any additional information about the client..."
                style={{
                  background: 'var(--surface-glass)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)'
                }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        
        <Modal.Footer style={{
          background: 'var(--surface-glass)',
          borderTop: '1px solid var(--border-color)'
        }}>
          <Button 
            variant="outline-secondary" 
            onClick={handleCloseModal}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={(e) => {
              e.preventDefault();
              const form = e.target.closest('.modal-content').querySelector('form');
              if (form) {
                form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
              }
            }}
            disabled={submitting}
            className="d-flex align-items-center gap-2"
          >
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" />
                {isEditing ? 'Updating...' : 'Adding...'}
              </>
            ) : (
              <>
                {isEditing ? <FaEdit /> : <FaUserPlus />}
                {isEditing ? 'Update Client' : 'Add Client'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Client Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered>
        <Modal.Header closeButton style={{
          background: 'var(--surface-glass)',
          borderBottom: '1px solid var(--border-color)'
        }}>
          <Modal.Title style={{ color: 'var(--text-primary)', fontWeight: 700 }}>
            Client Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: 'var(--bg-primary)' }}>
          {selectedClient && (
            <div>
              <div className="mb-3">
                <label style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  NAME
                </label>
                <p style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {selectedClient.name}
                </p>
              </div>
              
              <div className="mb-3">
                <label style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  PHONE NUMBER
                </label>
                <p style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
                  {selectedClient.phone}
                </p>
              </div>
              
              <div className="mb-3">
                <label style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  CONTACT PERSON
                </label>
                <p style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
                  {selectedClient.contactPerson || <span style={{ fontStyle: 'italic' }}>No contact person provided</span>}
                </p>
              </div>
              
              <div className="mb-3">
                <label style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  CONTACT PERSON PHONE
                </label>
                <p style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
                  {selectedClient.contactPhone || <span style={{ fontStyle: 'italic' }}>No contact person phone provided</span>}
                </p>
              </div>
              
              <div className="mb-3">
                <label style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  BRANCH
                </label>
                <p style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
                  {selectedClient.branch || <span style={{ fontStyle: 'italic' }}>No branch provided</span>}
                </p>
              </div>
              
              <div className="mb-3">
                <label style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  REGION
                </label>
                <p style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
                  {selectedClient.region || <span style={{ fontStyle: 'italic' }}>No region provided</span>}
                </p>
              </div>
              
              <div className="mb-3">
                <label style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  SITE
                </label>
                <p style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
                  {selectedClient.siteName}
                </p>
              </div>
              
              <div className="mb-3">
                <label style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  PHYSICAL LOCATION
                </label>
                <p style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
                  {selectedClient.physicalLocation || <span style={{ fontStyle: 'italic' }}>No location provided</span>}
                </p>
                {(selectedClient.locationName || selectedClient.locationCoords) && (
                  <div className="mt-1 p-2" style={{
                    background: 'var(--surface-glass)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '0.875rem'
                  }}>
                    {selectedClient.locationName && (
                      <div style={{ color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
                        <FaMapMarkerAlt style={{ color: 'var(--accent-blue)', marginRight: '0.4rem' }} />
                        {selectedClient.locationName}
                      </div>
                    )}
                    {selectedClient.locationCoords && (
                      <div style={{ color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        📍 {selectedClient.locationCoords}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="mb-3">
                <label style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  DESCRIPTION
                </label>
                <p style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
                  {selectedClient.description || <span style={{ fontStyle: 'italic' }}>No description provided</span>}
                </p>
              </div>
              
              <div className="mb-3">
                <label style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  ADDED BY
                </label>
                <p style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
                  {selectedClient.personnelName || 'Unknown'}
                </p>
              </div>
              
              <div>
                <label style={{ fontWeight: 600, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  DATE ADDED
                </label>
                <p style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
                  {selectedClient.createdAt ? new Date(selectedClient.createdAt).toLocaleString() : 'Unknown'}
                </p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{
          background: 'var(--surface-glass)',
          borderTop: '1px solid var(--border-color)'
        }}>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default NewClients;
